'use client';

/**
 * Skeleton loader components with shimmer effect
 * Used to show loading states for messages and product cards
 */

export function SkeletonMessage({ isBot = true }: { isBot?: boolean }) {
  return (
    <div className={`flex w-full mb-4 animate-message-slide-up ${isBot ? 'justify-start' : 'justify-end'}`}>
      <div className={`flex flex-col max-w-full md:max-w-[95%] ${isBot ? 'items-start' : 'items-end'} w-full`}>
        <div
          className={`
            relative px-6 py-4 rounded-2xl
            ${isBot ? 'bg-card rounded-tl-sm' : 'bg-black/10 dark:bg-white/10 rounded-tr-sm'}
          `}
        >
          {/* Avatar and Name skeleton */}
          <div className={`flex items-center gap-2 mb-2 ${isBot ? 'flex-row' : 'flex-row-reverse'}`}>
            <div className="w-5 h-5 rounded-lg bg-muted animate-pulse" />
            <div className="w-16 h-2 bg-muted rounded animate-pulse" />
          </div>
          
          {/* Text skeleton */}
          <div className="space-y-2">
            <div className="w-full h-3 bg-muted rounded animate-pulse" />
            <div className="w-4/5 h-3 bg-muted rounded animate-pulse" />
            <div className="w-3/4 h-3 bg-muted rounded animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function SkeletonProductCard() {
  return (
    <div className="flex w-full mb-4 animate-message-slide-up justify-start">
      <div className="flex flex-col w-full max-w-full items-start">
        <div className="bg-card rounded-[1.5rem] overflow-hidden w-full">
          {/* Image skeleton */}
          <div className="relative h-44 w-full bg-muted animate-pulse" />
          
          <div className="px-5 pt-4 pb-[0.4rem]">
            {/* Product name and price skeleton */}
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="flex-1 space-y-2">
                <div className="w-3/4 h-4 bg-muted rounded animate-pulse" />
                <div className="w-1/2 h-4 bg-muted rounded animate-pulse" />
              </div>
              <div className="w-20 h-4 bg-muted rounded animate-pulse" />
            </div>
            
            {/* Reasoning skeleton */}
            <div className="space-y-2 mt-2">
              <div className="w-full h-3 bg-muted rounded animate-pulse" />
              <div className="w-4/5 h-3 bg-muted rounded animate-pulse" />
            </div>
            
            {/* Features and button skeleton */}
            <div className="flex items-center justify-between gap-3 mt-3">
              <div className="w-24 h-6 bg-muted rounded-lg animate-pulse" />
              <div className="w-28 h-9 bg-muted rounded-xl animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SkeletonQuickReplies() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 w-full">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="h-12 bg-muted rounded-xl animate-pulse"
        />
      ))}
    </div>
  );
}

/**
 * Loading spinner for inline use
 */
export function LoadingSpinner({ size = 20, className = '' }: { size?: number; className?: string }) {
  return (
    <div className={`inline-block ${className}`}>
      <svg
        className="animate-spin"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
}
